<template>
  <v-autocomplete
      v-model="internalValue"
      :items="items"
      :loading="isLoading"
      :label="label || $t('sportCenter')"
      :placeholder="placeholder || $t('startTypingToSearch')"
      :item-text="itemText"
      :item-value="itemValue"
      :multiple="multiple"
      :clearable="clearable"
      :disabled="disabled"
      :return-object="returnObject"
      :search-input.sync="search"
      :filter="customFilter"
      hide-details
      chips
      small-chips
      deletable-chips
      @change="emitChange"
      hide-no-data
      hide-selected
      outlined
      dense
  />
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'CenterSelectSearch',
  props: {
    value: { default: null },                  // ID o array di ID (o oggetti se returnObject=true)
    initValue: { type: [String, Array], default: null },
    multiple: { type: Boolean, default: false },
    label: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    clearable: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    // auto: 'user' -> prova user.center_id; 'single' -> unico centro; 'first' -> primo; 'none' -> niente
    autoStrategy: { type: String, default: 'user' },
    includeAllOption: { type: Boolean, default: false }, // solo per selezione singola
    itemText: { type: String, default: 'name' },
    itemValue: { type: String, default: '_id' },
    returnObject: { type: Boolean, default: false }      // se true, v-model = oggetto, altrimenti ID
  },
  data () {
    return {
      isLoading: false,
      list: [],
      internalValue: this.multiple ? [] : null,
      search: '',
      _autoApplied: false
    };
  },
  computed: {
    ...mapGetters({ getUser: 'user/getUser' }),
    items () {
      // normalizza name da desc se serve
      var arr = Array.isArray(this.list) ? this.list.slice() : [];
      for (var i = 0; i < arr.length; i++) {
        if (!arr[i].name && arr[i].desc) arr[i].name = arr[i].desc;
      }
      if (!this.multiple && this.includeAllOption) {
        var opt = {};
        opt[this.itemText]  ='Tutti';
        opt[this.itemValue] = null;
        arr.unshift(opt);
      }
      return arr;
    }
  },
  watch: {
    value (v) {
      // sincr. esterno -> interno
      if (this.multiple) {
        this.internalValue = Array.isArray(v) ? v.slice() : [];
      } else {
        this.internalValue = typeof v === 'undefined' ? null : v;
      }
    }
  },
  created () {
    // init internal da value/initValue
    if (typeof this.value !== 'undefined' && this.value !== null) {
      this.internalValue = this.multiple ? (Array.isArray(this.value) ? this.value.slice() : []) : this.value;
    } else if (this.initValue) {
      this.internalValue = this.multiple ? (Array.isArray(this.initValue) ? this.initValue.slice() : [this.initValue]) : this.initValue;
    }
  },
  mounted () {
    this.bootstrap();
  },
  methods: {
    ...mapActions({ getList: 'centers/getList' }),

    async bootstrap () {
      this.isLoading = true;
      try {
        var data = await this.getList(); // deve tornare [{ _id, name, ... }]
        this.list = Array.isArray(data) ? data : [];

        // se non è selezionato nulla, prova autoselezione
        var hasVal = this.multiple
            ? (Array.isArray(this.internalValue) && this.internalValue.length > 0)
            : !!this.internalValue || this.internalValue === null && this.includeAllOption;

        if (!hasVal && !this._autoApplied) this.applyAutoSelect();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[CenterSelectSearch] load error:', e);
      } finally {
        this.isLoading = false;
      }
    },

    applyAutoSelect () {
      this._autoApplied = true;
      if (this.multiple) return;

      // se già presente un valore, non toccare
      if (this.internalValue || this.internalValue === null && this.includeAllOption) return;

      var list = this.items;
      if (!Array.isArray(list) || list.length === 0) return;

      if (this.autoStrategy === 'user') {
        var cid = this.getUser && this.getUser.center_id ? this.getUser.center_id : null;
        if (cid) {
          var found = this._findById(list, cid, this.itemValue);
          if (found) return this._commit(found[this.itemValue]);
        }
        if (list.length === 1) return this._commit(list[0][this.itemValue]);
        return;
      }

      if (this.autoStrategy === 'single') {
        if (list.length === 1) return this._commit(list[0][this.itemValue]);
        return;
      }

      if (this.autoStrategy === 'first') {
        return this._commit(list[0][this.itemValue]);
      }
    },

    _commit (val) {
      // per returnObject: se true, trova e assegna l’oggetto; altrimenti assegna l’ID
      if (this.returnObject) {
        var obj = this._findById(this.items, val, this.itemValue);
        this.internalValue = obj || null;
      } else {
        this.internalValue = this.multiple ? (Array.isArray(val) ? val.slice() : [val]) : val;
      }
      this.emitChange();
    },

    _findById (arr, id, key) {
      for (var i = 0; i < arr.length; i++) {
        if (arr[i][key] === id) return arr[i];
      }
      return null;
    },

    customFilter (item, queryText, itemText) {
      // ricerca su name/desc case-insensitive
      var q = (queryText || '').toString().toLowerCase();
      if (!q) return true;
      var txt = (item[this.itemText] || '').toString().toLowerCase();
      var desc = (item.desc || '').toString().toLowerCase();
      return txt.indexOf(q) > -1 || desc.indexOf(q) > -1;
    },

    emitChange () {
      // 1) v-model
      this.$emit('input', this.internalValue);

      // 2) 'message' sempre come ID o array di ID (retro-compat con tuo setCenterId)
      var out;
      if (this.multiple) {
        if (this.returnObject) {
          out = Array.isArray(this.internalValue) ? this.internalValue.map(o => o ? o[this.itemValue] : null) : [];
        } else {
          out = Array.isArray(this.internalValue) ? this.internalValue.slice() : [];
        }
      } else {
        if (this.returnObject) {
          out = this.internalValue ? this.internalValue[this.itemValue] : '';
        } else {
          out = this.internalValue || '';
        }
      }
      this.$emit('message', out);
      this.$emit('change', out);
    }
  }
};
</script>
